import { type Permission } from '@convo/core';

/**
 * 一個 workspace 的角色（E5-07-02）。
 *
 * 角色不再是寫死的三個 enum 值——它是資料庫裡的一列，每個 workspace 可以有自己的組合。
 * 下拉選單一律吃 `listRoles()` 的結果，不要再從 `WORKSPACE_ROLES` 產生。
 */
/** 建立／編輯角色送出去的形狀。 */
export interface RoleInput {
  key: string;
  name: string;
  description: string | null;
  permissions: Permission[];
  conversationScope: 'all' | 'own_and_unassigned';
  channelIds: string[];
  maskContactPii: boolean;
}

export interface RoleSummary {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** 內建角色（owner／admin／agent）：不可改權限、不可刪。 */
  isSystem: boolean;
  permissions: Permission[];
  conversationScope: 'all' | 'own_and_unassigned';
  channelIds: string[];
  maskContactPii: boolean;
}

import { apiDelete, apiGet, apiPatch, apiPost } from './http';

/** 成員摘要，用在指派下拉與（P1-08 的）成員管理頁。 */
export interface MemberSummary {
  id: string;
  userId: string;
  roleId: string;
  /** 角色 key。判斷是不是內建角色時用，權限判斷一律看 permissions。 */
  role: string;
  roleName: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  createdAt: string;
}

export interface InvitationSummary {
  id: string;
  email: string;
  roleId: string;
  /** 角色 key。判斷是不是內建角色時用，權限判斷一律看 permissions。 */
  role: string;
  roleName: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedBy?: string;
  expiresAt: string;
  createdAt?: string;
  /** 只有剛建立時會回傳一次——寄信失敗時管理者還是能把連結傳給對方。 */
  link?: string;
  emailDelivered?: boolean;
}

export interface InvitationPreview {
  workspaceName: string;
  email: string;
  roleId: string;
  /** 角色 key。判斷是不是內建角色時用，權限判斷一律看 permissions。 */
  role: string;
  roleName: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
}

export interface MemberListItem extends MemberSummary {
  lastSeenAt?: string | null;
}

/** 對應 apps/api 的 WorkspaceDetail。 */
export interface WorkspaceDetail {
  id: string;
  name: string;
  /** 報表的「一天」以哪個時區為準（E2-07-01）。 */
  timezone: string;
  /** 歸因窗口（E3-06-04）：訂單往回看幾天內的點擊。改了只影響之後的訂單。 */
  attributionWindowDays: number;
  /** SLA：首次回應要在幾分鐘內（E4-05-03）；null＝關閉。 */
  slaFirstResponseMinutes: number | null;
  createdAt: string;
  memberCount: number;
}

export const workspacesApi = {
  detail: (workspaceId: string) => apiGet<WorkspaceDetail>(`/workspaces/${workspaceId}`),

  update: (
    workspaceId: string,
    input: {
      name?: string;
      timezone?: string;
      attributionWindowDays?: number;
      /** null＝把 SLA 關掉；不帶＝不動它。 */
      slaFirstResponseMinutes?: number | null;
    },
  ) => apiPatch<WorkspaceDetail>(`/workspaces/${workspaceId}`, input),

  members: (workspaceId: string) => apiGet<MemberListItem[]>(`/workspaces/${workspaceId}/members`),

  /** Onboarding 進度（E5-02-04）：四步驟的真實狀態。 */
  onboarding: (workspaceId: string) =>
    apiGet<{ hasChannel: boolean; hasInbound: boolean; hasAutomation: boolean; members: number }>(
      `/workspaces/${workspaceId}/onboarding`,
    ),

  listRoles: (workspaceId: string) => apiGet<RoleSummary[]>(`/workspaces/${workspaceId}/roles`),

  createRole: (workspaceId: string, input: RoleInput) =>
    apiPost<RoleSummary>(`/workspaces/${workspaceId}/roles`, input),

  /** 改角色本身的定義。跟 `updateRole`（把某個成員換成某個角色）是兩件事。 */
  editRole: (workspaceId: string, roleId: string, input: Partial<RoleInput>) =>
    apiPatch<RoleSummary>(`/workspaces/${workspaceId}/roles/${roleId}`, input),

  deleteRole: (workspaceId: string, roleId: string) =>
    apiDelete<{ id: string }>(`/workspaces/${workspaceId}/roles/${roleId}`),

  updateRole: (workspaceId: string, userId: string, roleId: string) =>
    apiPatch<{ userId: string; roleId: string; role: string; roleName: string }>(
      `/workspaces/${workspaceId}/members/${userId}`,
      { roleId },
    ),

  removeMember: (workspaceId: string, userId: string) =>
    apiDelete<{ userId: string; unassignedConversations: number }>(
      `/workspaces/${workspaceId}/members/${userId}`,
    ),

  /**
   * 幫成員發密碼重設連結。
   *
   * 回應帶明文 `link`——這支存在的理由就是「信這條路不通」（email 填錯、進垃圾桶），
   * 所以 UI 一定要把連結顯示出來讓管理員自己轉交，不能只說「已寄出」。
   */
  sendPasswordReset: (workspaceId: string, userId: string) =>
    apiPost<{
      userId: string;
      email: string;
      link: string;
      expiresAt: string;
      emailDelivered: boolean;
    }>(`/workspaces/${workspaceId}/members/${userId}/reset-password`, {}),

  invitations: (workspaceId: string) =>
    apiGet<InvitationSummary[]>(`/workspaces/${workspaceId}/invitations`),

  invite: (workspaceId: string, input: { email: string; roleId: string }) =>
    apiPost<InvitationSummary>(`/workspaces/${workspaceId}/invitations`, input),

  revokeInvitation: (workspaceId: string, id: string) =>
    apiDelete<{ id: string }>(`/workspaces/${workspaceId}/invitations/${id}`),

  /** 邀請連結：受邀者還沒登入時也看得到。 */
  previewInvitation: (token: string) => apiGet<InvitationPreview>(`/invitations/${token}`),

  acceptInvitation: (token: string) =>
    apiPost<{ workspaceId: string; roleId: string; role: string; roleName: string }>(
      `/invitations/${token}/accept`,
    ),
};
